import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dbSelect, dbRpc } from '../lib/api';

export interface StarredProject {
  id: string;
  project_id: string;
  name: string;
  description: string | null;
  type: string | null;
  slug: string | null;
  public_id: string | null;
  required_tier: string | null;
  starred_at: string;
}

export function useStarredProjects() {
  const { user, session } = useAuth();
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set());
  const [starredProjects, setStarredProjects] = useState<StarredProject[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const token = session?.access_token;

  // Lightweight fetch: just project IDs for isStarred() checks
  const fetchIds = useCallback(async () => {
    if (!user || !token) {
      setStarredIds(new Set());
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await dbSelect<{ project_id: string }[]>('starred_projects', {
        token,
        select: 'project_id',
        filters: { user_id: `eq.${user.id}` },
      });

      if (error) throw error;
      setStarredIds(new Set((data || []).map((r) => r.project_id)));
    } catch (err) {
      console.error('Failed to fetch starred IDs:', err);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Full fetch: project details for the StarredPage
  const fetchStarred = useCallback(async () => {
    if (!user || !token) {
      setStarredProjects([]);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await dbRpc<StarredProject[]>('get_starred_projects', {}, { token });
      if (error) throw error;
      setStarredProjects(data || []);
      setStarredIds(new Set((data || []).map((r) => r.project_id)));
    } catch (err) {
      console.error('Failed to fetch starred projects:', err);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Fetch IDs on mount
  useEffect(() => {
    if (user && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchIds();
    }
  }, [user, fetchIds]);

  // Reset when user changes
  useEffect(() => {
    fetchedRef.current = false;
  }, [user?.id]);

  const isStarred = useCallback(
    (projectId: string) => starredIds.has(projectId),
    [starredIds]
  );

  const toggleStar = useCallback(
    async (projectId: string) => {
      if (!user || !token) return { error: 'Not authenticated' };

      const wasStarred = starredIds.has(projectId);

      // Optimistic update
      setStarredIds((prev) => {
        const next = new Set(prev);
        if (wasStarred) {
          next.delete(projectId);
        } else {
          next.add(projectId);
        }
        return next;
      });

      if (wasStarred) {
        setStarredProjects((prev) => prev.filter((p) => p.project_id !== projectId));
      }

      const { data: isNowStarred, error } = await dbRpc<boolean>('toggle_star', { p_project_id: projectId }, { token });

      if (error) {
        // Revert on error
        setStarredIds((prev) => {
          const next = new Set(prev);
          if (wasStarred) {
            next.add(projectId);
          } else {
            next.delete(projectId);
          }
          return next;
        });
        return { error: error.message };
      }

      // Sync with authoritative DB state from RPC response.
      setStarredIds((prev) => {
        const next = new Set(prev);
        if (isNowStarred) {
          next.add(projectId);
        } else {
          next.delete(projectId);
        }
        return next;
      });

      if (isNowStarred === false) {
        setStarredProjects((prev) => prev.filter((p) => p.project_id !== projectId));
      }

      return { error: null };
    },
    [user, token, starredIds]
  );

  return { starredProjects, isStarred, toggleStar, loading, fetchStarred };
}
